// @flow

import React from 'react';
import { I18n } from 'react-redux-i18n';
import { isUndefined, isUSDorGBP } from './utils';
import { yearToMonthAndDay } from './dateUtils';
import CurrencyConvertor from '../pages/common/CurrencyConvertor';

export const formatYearMonth = (year: number, month: number) => (
  `${year} - ${month > 9 ? month : `0${month}`}`
);

export const splitThousand = (
  number: number | string,
  currency: ?string,
  digits?: number,
): string => {
  if (isUndefined(number)) {
    return 'N/A';
  }
  if (currency === 'USD' || currency === 'GBP') {
    const result = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (result.indexOf('.') >= 0) {
      const tmp = result.split('.');
      return ` ${tmp[0]}.${tmp[1].replace(',', '').substring(0, 2)}`;
    }
    return result;
  }
  const result = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  if (result.indexOf('.') >= 0) {
    const tmp = result.split('.');

    return ` ${tmp[0]}.${tmp[1].substring(0, digits || 2)}`;
  }
  return result;
};

export const roundTenthSuperior = (number: number | string, digits: number): string => {
  if (isUndefined(number)) {
    return 'N/A';
  }
  let arrondi = number * Math.pow(10, digits);
  arrondi = Math.ceil(arrondi);
  return ` ${arrondi / Math.pow(10, digits)}`;
};

export function capitalizeLabel(label: string) {
  if (!label) {
    return null;
  }
  const tmp = label.toLowerCase();
  return tmp.charAt(0).toUpperCase() + tmp.slice(1);
}

export function upToThousandUnit(number: string, unit: string, abs: boolean = true) {
  const absF = abs ? Math.abs : (x => x);
  if (number !== undefined) {
    switch (unit) {
      case 'K':
        return (absF(Number(number)) / 1.0e+3);
      case 'M':
        return (absF(Number(number)) / 1.0e+6);
      case 'B':
        return (absF(Number(number)) / 1.0e+9);
      default:
        return Number(number);
    }
  }

  return Number(number);
}

export function autoQuantityCurrencyString(number: string, digits?: number): string {
  return `${autoQuantityString(number, digits)}€`;
}

export function autoQuantityMinutesString(number: string): string {
  return `${autoQuantityString(number, 0)} mins`;
}

export function autoQuantityString(number: string, digits?: number): string {
  const q = autoQuantity(number);
  const nbDigits = digits !== undefined ? digits : 2;
  return `${q.value.toFixed(nbDigits)} ${q.quantity}`;
}

export function autoQuantity(number: string): string {
  const category = Math.min(Math.floor(Math.log10(Math.abs(number)) / 3), 3);
  const units = { 1: 'K', 2: 'M', 3: 'B' };
  const unit = units[category] || '';
  return { value: upToThousandUnit(formatThousand(number), unit, false), quantity: unit };
}

export function autoQuantityStr(number: number): string {
  const quantity = autoQuantity(number);
  return `${quantity.value.toFixed(2)}${quantity.quantity}`;
}

export function getPrettifyValue(value: number, digits?: number): string {
  return value <= 1 ? convertToPercentile(value, digits) : `${value.toFixed(digits || 3)}`;
}

export function convertToPercentile(number: number, digits?: number): string {
  const value = Number(number);
  if (number == null || Number.isNaN(value)) {
    return 'N/A';
  }
  return `${(value * 100).toFixed(digits || 3)}%`;
}

export const formatRoi = (roi, netMgrGlaUplift, tic) => {
  if (netMgrGlaUplift < 0) {
    return I18n.t('roi.kpi.negativeUplift');
  }
  if (tic < 0) {
    return I18n.t('roi.kpi.negativeTic');
  }
  return convertToPercentile(roi, 2);
};

export const computePayback = (mgr, tic) => {
  if (tic <= 0) {
    return 0;
  }
  if (mgr === 0) {
    return NaN;
  }
  return tic / mgr;
};

export const formatPayback = (payback: number) => {
  let newPayback = payback;
  if (Number.isNaN(newPayback)) {
    return '-';
  }
  if (newPayback <= 0) {
    return '0 yr';
  }
  let quantity = 'yrs';
  if (newPayback < 1) {
    newPayback *= 12;
    quantity = 'mths';
  }
  return `${Math.round(newPayback * 100) / 100} ${quantity}`;
};

export function roundValue(value: number, currency: ?string, digits?: number): string {
  if (isUndefined(value)) {
    return 'N/A';
  }
  const isValueMillion = Math.abs(value) >= 1000000;
  const shownValue = isValueMillion ? value / 1000000 : value;
  const fractionDigits = (digits != null) ? digits : getDefaultDigits(shownValue);
  return splitThousand(shownValue.toFixed(fractionDigits), currency) + (isValueMillion ? ' M' : '');
}

export const getDefaultDigits = (value: number): number => Math.abs(value >= 100 ? 0 : 1);

export const roundPercentage = (value: number, digits?: number): string =>
  `${value.toFixed(digits || 0)}%`;

export const formatThousand = (value: number) => (Math.abs(value) >= 10000
  ? Math.round(value / 100) * 100
  : value);

export const toFixedOrElse = (value: number, digits?: number, orElse: any) => {
  const finalDigits = digits || 2;
  if (!value) {
    return orElse;
  }
  return value.toFixed(finalDigits);
};


export const formatMgr = (netMgrGlaUplift: number, digits?: number) => {
  if (netMgrGlaUplift === 0) {
    return '-';
  }
  return (<CurrencyConvertor {...autoQuantity(netMgrGlaUplift)} digits={digits} />);
};

export const formatTic = (tic: number, digits?: number) => {
  const newTic = Math.max(tic, 0);
  return (
    <CurrencyConvertor {...autoQuantity(newTic)} digits={digits} />
  );
};

export const formatNaN = (number: number | string) => (Number.isNaN(number) ? 0 : number);

export const formatSuggestionMgr = (netMgrGlaUplift: number) =>
  (netMgrGlaUplift !== 0 ? netMgrGlaUplift : '-');

export const formatSuggestionTic = (tic: number) => Math.max(tic, 0);

export const formatRoiParam = (param: number, digits?: number) => {
  if (param === -0.0) {
    param = 0.0;
  }
  return <CurrencyConvertor {...autoQuantity(param)} digits={digits} />;
};

export const formatRentFreePeriod = (x: number) => {
  const monthAndDay = yearToMonthAndDay(x);
  return `${monthAndDay.month}M ${monthAndDay.day}D`;
};

export const displayTva = x => (`${formatTva(x)}%`);

export const formatExitCap = x => (`${x.toFixed(2)}`);

export const truncPercentTwo = x => Math.round(x * 10000) / 100;
export const undoTruncPercentTwo = x => x / 100;

export const formatMinute = (minutes: number) => `${Math.round(minutes)} minutes`;

export const formatMeter = (meter: number) => `${Math.round(meter)} m`;

export const removeAccents = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const removeSpaces = text => text.replace(/\s/g, '');

export const addDimensionSymbol = (dimensionSymbol: string, isCurrencyBySurface: boolean): string =>
  (isCurrencyBySurface ? (`/${dimensionSymbol}`) : '');

export const addCurrencySymbol = (convertedValue: string, symbol: string, currency: string): string => {
  if (isUSDorGBP(currency)) {
    return `${symbol} ${convertedValue}`;
  }
  return `${convertedValue} ${symbol}`;
};

export const formatMoneyValueWithSymbol = (value: string, symbol: string, currency: string, quantity: string): string =>
  `${isUSDorGBP(currency) ? symbol : ''} ${value} ${quantity} ${!isUSDorGBP(currency) ? symbol : ''}`;


export const formatTva = x => Math.round(((x - 1) * 10000) / 100);
export const undoFormatTva = x => 1 + (x / 100);
